import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import type { CognitoUser, CreateUserInput, UpdateUserInput } from '../../types/cognitoUser';

const COGNITO_GROUPS = [
  { label: 'GS-ADMIN', value: 'GS-ADMIN' },
  { label: 'GS-USER', value: 'GS-USER' },
  { label: 'GS-ORGANIZATION', value: 'GS-ORGANIZATION' },
  { label: 'GS-USER-ADMIN', value: 'GS-USER-ADMIN' },
  { label: 'READ-ONLY-USER', value: 'READ-ONLY-USER' },
];

// eslint-disable-next-line no-control-regex, no-useless-escape
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface UserDetailFormProps {
  visible: boolean;
  user: CognitoUser | null;
  onHide: () => void;
  onCreateUser: (input: CreateUserInput) => Promise<CognitoUser | null>;
  onUpdateUser: (input: UpdateUserInput) => Promise<CognitoUser | null>;
  onDeleteUser: (username: string) => Promise<boolean>;
  onDisableUser: (username: string) => Promise<CognitoUser | null>;
  onEnableUser: (username: string) => Promise<CognitoUser | null>;
}

export const UserDetailForm: React.FC<UserDetailFormProps> = ({
  visible,
  user,
  onHide,
  onCreateUser,
  onUpdateUser,
  onDeleteUser,
  onDisableUser,
  onEnableUser,
}) => {
  const isEditMode = !!user;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [organization, setOrganization] = useState('');
  const [groups, setGroups] = useState<string[]>([]);
  const [emailError, setEmailError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when dialog opens or user changes
  useEffect(() => {
    if (visible) {
      if (user) {
        setEmail(user.email || '');
        setName(user.name || '');
        setUsername(user.username || '');
        setOrganization(user.organization || '');
        setGroups(user.groups || []);
      } else {
        setEmail('');
        setName('');
        setUsername('');
        setOrganization('');
        setGroups([]);
      }
      setEmailError('');
      setFormError('');
    }
  }, [visible, user]);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError('Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) return;
    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      if (isEditMode) {
        await onUpdateUser({ username: user!.username, email, name, groups, organization: organization || undefined });
      } else {
        console.log('=== Creating user with:', { email, name, username, groups, organization });
        const result = await onCreateUser({ email, name, groups, username: username || undefined, organization: organization || undefined } as any);
        console.log('=== Create result:', result);
      }
      onHide();
    } catch (error) {
      console.error('=== Create user error:', error);
      const message = error instanceof Error ? error.message : 'Operation failed';
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (!user) return;
    confirmDialog({
      message: `Are you sure you want to permanently delete user "${user.username}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setSubmitting(true);
        setFormError('');
        try {
          await onDeleteUser(user.username);
          onHide();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Delete failed';
          setFormError(message);
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const handleToggleEnabled = async () => {
    if (!user) return;
    setSubmitting(true);
    setFormError('');
    try {
      if (user.enabled) {
        await onDisableUser(user.username);
      } else {
        await onEnableUser(user.username);
      }
      onHide();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Operation failed';
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {isEditMode && (
          <>
            <Button
              label={user?.enabled ? 'Disable' : 'Enable'}
              icon={user?.enabled ? 'pi pi-ban' : 'pi pi-check-circle'}
              className={user?.enabled ? 'p-button-warning' : 'p-button-success'}
              onClick={handleToggleEnabled}
              disabled={submitting}
            />
            {!user?.enabled && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                className="p-button-danger"
                onClick={handleDelete}
                disabled={submitting}
              />
            )}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-text"
          onClick={onHide}
          disabled={submitting}
        />
        <Button
          label={isEditMode ? 'Update' : 'Create'}
          icon="pi pi-check"
          onClick={handleSubmit}
          loading={submitting}
        />
      </div>
    </div>
  );

  return (
    <>
      <ConfirmDialog />
      <Dialog
        header={isEditMode ? `Edit User: ${user?.username}` : 'Create User'}
        visible={visible}
        onHide={onHide}
        footer={footer}
        style={{ width: '450px' }}
        modal
        closable={!submitting}
        draggable={false}
      >
        {formError && (
          <Message severity="error" text={formError} style={{ width: '100%', marginBottom: '1rem' }} />
        )}

        <div className="flex flex-column gap-3" style={{ padding: '0.5rem 0' }}>
          {/* Email */}
          <div className="flex flex-column gap-1">
            <label htmlFor="user-email" style={{ fontWeight: 600 }}>Email</label>
            <InputText
              id="user-email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
                // Auto-derive username from email in create mode (if user hasn't manually edited it)
                if (!isEditMode) {
                  const localPart = e.target.value.split('@')[0];
                  setUsername(localPart);
                }
              }}
              onBlur={() => validateEmail(email)}
              className={emailError ? 'p-invalid' : ''}
              placeholder="user@example.com"
              disabled={submitting}
            />
            {emailError && <small className="p-error">{emailError}</small>}
          </div>

          {/* Username */}
          <div className="flex flex-column gap-1">
            <label htmlFor="user-username" style={{ fontWeight: 600 }}>Username</label>
            <InputText
              id="user-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
              disabled={isEditMode || submitting}
            />
            {!isEditMode && <small className="text-500">Auto-derived from email. You can override it.</small>}
          </div>

          {/* Name */}
          <div className="flex flex-column gap-1">
            <label htmlFor="user-name" style={{ fontWeight: 600 }}>Name</label>
            <InputText
              id="user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              disabled={submitting}
            />
          </div>

          {/* Organization */}
          <div className="flex flex-column gap-1">
            <label htmlFor="user-organization" style={{ fontWeight: 600 }}>Organization</label>
            <InputText
              id="user-organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="Organization name"
              disabled={submitting}
            />
          </div>

          {/* Groups */}
          <div className="flex flex-column gap-1">
            <label htmlFor="user-groups" style={{ fontWeight: 600 }}>Groups</label>
            <MultiSelect
              id="user-groups"
              value={groups}
              options={COGNITO_GROUPS}
              onChange={(e) => setGroups(e.value)}
              placeholder="Select groups"
              display="chip"
              disabled={submitting}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};
